<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h2" class="orangehrm-main-title">
          {{ custName }}: Invoice - {{ monthName }}/{{ deductions.taxYear }}
        </oxd-text>
        <div v-if="past">
          <oxd-button
            :label="$t('pim.gen_invoice')"
            icon-name="plus"
            display-type="secondary"
            @click="onClickAdd"
          />
        </div>
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :loading="isLoading"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
        <oxd-text tag="h2" class="orangehrm-main-title">
          Total Amount: ${{ totalCostSum.toFixed(2) }}
        </oxd-text>
      </div>
      <oxd-grid :cols="2" v-if="totalCostSum > 0">
        <oxd-grid-item>
          <oxd-button
            :label="$t('Download Invoice')"
            display-type="secondary"
            @click="onClickDownload"
          />
        </oxd-grid-item>
        <oxd-grid-item> </oxd-grid-item>
      </oxd-grid>
      <oxd-divider />
      <invoice-payment-vue
        v-if="
          custName && totalCostSum && invoiceNumber && custId && invoiceDate
        "
        :custName="custName"
        :custId="custId"
        :invNumber="invoiceNumber"
        :invDate="invoiceDate"
        :invTotal="totalCostSum"
      ></invoice-payment-vue>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {OxdSwitchInput} from '@ohrm/oxd';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import InvoicePaymentVue from '../ExpenseTypes/InvoicePayments.vue';

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'invoice-payment-vue': InvoicePaymentVue,
  },
  props: {
    cust: {
      type: Number,
      required: true,
    },
    month: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    //console.log(props);
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/invoices/cust/' + props.cust + '/month/' + props.month,
    );
    const http1 = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/deductions',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http);
    return {
      http,
      http1,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
    };
  },
  data() {
    return {
      monthName: null,
      monthId: null,
      custId: null,
      custName: null,
      invoiceNumber: null,
      invoiceDate: null,
      yearsArray: [...yearRange()],
      full: false,
      part: false,
      deductions: {
        taxYear: '',
      },
      past: false,
      headers: [
        {
          name: 'empName',
          slot: 'title',
          title: this.$t('general.employee'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'shiftDate',
          slot: 'title',
          title: this.$t('general.date'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'lineType',
          slot: 'title',
          title: 'Type',
          style: {'flex-basis': '80%'},
        },
        {
          name: 'shiftHours',
          slot: 'title',
          title: this.$t('pim.shifthours'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'costHour',
          slot: 'title',
          title: this.$t('general.costhour'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'totalCost',
          slot: 'title',
          title: this.$t('general.amount'),
          style: {'flex-basis': '80%'},
        },
      ],
      checkedItems: [],
    };
  },
  mounted() {
    //this.getParameters();
    //this.getTaxYear();
  },
  created() {
    this.getTaxYear();
  },
  computed: {
    totalCostSum() {
      if (!Array.isArray(this.items?.data)) return 0;
      return (
        this.items.data.reduce(
          (sum, item) => sum + parseFloat(item.totalCost),
          0,
        ) || 0
      );
    },
  },
  methods: {
    async getTaxYear() {
      this.isLoading = true;
      this.http1.http
        .get('/api/v2/pim/deductions')
        .then((response) => {
          const {data} = response.data;
          this.deductions.taxYear = data.taxyear;
          this.getParameters(data.taxyear);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async getParameters(taxyear) {
      const monthsArray = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const url = window.location.href;
      const regex = /runInvoice\/cust\/(\d+)\/month\/(\d+)/;
      const matches = url.match(regex);
      const today = new Date();
      //const thisYear = new Date().getFullYear();
      const thisYear = taxyear;
      //console.log('Year get parameters' + thisYear);
      const todayDate = today.toISOString().split('T')[0];
      if (matches && matches.length >= 3) {
        this.custId = matches[1];
        this.monthId = matches[2];
        this.monthName = monthsArray[matches[2] - 1];
        let cutMonth = Number(matches[2]) + 1;
        cutMonth = cutMonth.toString();
        const cutDate = thisYear + '-' + cutMonth.padStart(2, '0') + '-05';
        if (todayDate < cutDate) {
          this.past = true;
        } else {
          this.past = true;
        }
        const str = '/api/v2/time/customers';
        const custs = new APIService(window.appGlobal.baseUrl, str);
        custs.getAll().then((response) => {
          let dat = response.data;
          const cust = dat.data.find((c) => c.id == this.custId);
          if (cust) {
            this.custName = cust.name;
            this.invoiceDate = this.getFromTo(this.monthId)[1];
            this.invoiceNumber =
              cust.name.substring(0, 3) +
              '-' +
              this.getFromTo(this.monthId)[0].substring(0, 7);
          }
        });
      }
    },
    getLastDayOfFebruary(year) {
      const marchFirst = new Date(year, 2, 1); // Month is 0-indexed, so 2 = March
      const lastDayOfFeb = new Date(marchFirst.getTime() - 86400000); // Subtract 24 hours in milliseconds
      const day = lastDayOfFeb.getDate().toString().padStart(2, '0');
      return `${year}-02-${day}`;
    },
    getFromTo(monthId) {
      //const year = new Date().getFullYear(); // Get the current year
      const year = this.deductions.taxYear; // Get the current year
      //console.log('Year get from to' + year);

      let from, to;
      monthId = Number(monthId); // Ensure monthId is a number
      switch (monthId) {
        case 1:
          from = `${year}-01-01`;
          to = `${year}-01-31`;
          break;
        case 2:
          from = `${year}-02-01`;
          to = `${year}-02-29`;
          to = this.getLastDayOfFebruary(year);
          break;
        case 3:
          from = `${year}-03-01`;
          to = `${year}-03-31`;
          break;
        case 4:
          from = `${year}-04-01`;
          to = `${year}-04-30`;
          break;
        case 5:
          from = `${year}-05-01`;
          to = `${year}-05-31`;
          break;
        case 6:
          from = `${year}-06-01`;
          to = `${year}-06-30`;
          break;
        case 7:
          from = `${year}-07-01`;
          to = `${year}-07-31`;
          break;
        case 8:
          from = `${year}-08-01`;
          to = `${year}-08-31`;
          break;
        case 9:
          from = `${year}-09-01`;
          to = `${year}-09-30`;
          break;
        case 10:
          from = `${year}-10-01`;
          to = `${year}-10-31`;
          break;
        case 11:
          from = `${year}-11-01`;
          to = `${year}-11-30`;
          break;
        case 12:
          from = `${year}-12-01`;
          to = `${year}-12-31`;
          break;
        default:
          from = '';
          to = '';
          break;
      }

      return [from, to];
    },
    onClickAdd() {
      this.isLoading = true;
      this.http
        .create({
          custId: this.custId,
          monthId: this.monthId,
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          navigate(
            '/pim/runInvoice/cust/' + this.custId + '/month/' + this.monthId,
          );
        });
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    onClickDownload(item) {
      const from = this.getFromTo(this.monthId)[0];
      const to = this.getFromTo(this.monthId)[1];
      const dash = '-';
      const pdf = '.pdf';
      const mel = 'Melbourne';
      const until = 'to';
      const inv = 'Invoice';
      const emp = '1';
      const fl = mel.concat(
        dash,
        this.custName,
        dash,
        from,
        dash,
        until,
        dash,
        to,
        dash,
        inv,
        pdf,
      );
      //const id = this.getFileId(fl, item.empNumber);
      const str1 = '/api/v2/pim/employees/';
      const str2 = '/screen/personal/attachments';
      const str = str1.concat(emp, str2);
      const sfiles = new APIService(window.appGlobal.baseUrl, str);
      console.log(fl);
      sfiles.getAll().then((response) => {
        let dat = response.data;
        const fle = dat.data.find((f) => f.filename === fl);
        console.log(fle);
        if (fle) {
          let id = fle.id;
          const downUrl = `${window.appGlobal.baseUrl}/pim/viewAttachment/empNumber/1/attachId/${id}`;
          window.open(downUrl, '_blank');
        }
      });
    },
    async resetDataTable() {
      await this.execQuery();
    },
  },
};
</script>
